import React, { useState } from "react";
import { Alert, Form, Input, Space, Typography, Steps, Descriptions } from "antd";
import {
  SafetyCertificateOutlined,
  MobileOutlined,
  ReloadOutlined,
  StopOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import { Button, Card, Badge, Modal } from "../../components/Common";
import { useMutations } from "../../hooks/useApiMutation";
import { setTwoFactorState } from "../../store/authSlice";
import RecoveryCodesModal from "./RecoveryCodesModal";

const { Text, Paragraph } = Typography;

/**
 * Authenticator enrolment for the signed-in account.
 *
 * Enrolment is deliberately two-phased: /2fa/setup only parks a candidate secret, and
 * nothing about the account changes until /2fa/enable proves a code generated from it.
 * Abandoning this card therefore never leaves anyone half-locked-out.
 */
const TwoFactorCard = ({ status, onChanged }) => {
  const dispatch = useDispatch();
  const { mutate, loading } = useMutations();

  const [setup, setSetup] = useState(null);       // { sharedKey, authenticatorUri, qrCodeDataUri }
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [disabling, setDisabling] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const [enableForm] = Form.useForm();
  const [codeForm] = Form.useForm();

  const enabled = Boolean(status?.enabled);
  const required = Boolean(status?.required);

  const publish = (isEnabled) => {
    dispatch(setTwoFactorState({ enabled: isEnabled, setupRequired: required && !isEnabled }));
    onChanged?.();
  };

  const startSetup = () =>
    mutate("startTwoFactorSetup", {
      method: "post",
      endpoint: "/account/2fa/setup",
      onSuccess: (data) => {
        enableForm.resetFields();
        setSetup(data);
      },
    });

  const confirmSetup = async () => {
    const values = await enableForm.validateFields();
    await mutate("enableTwoFactor", {
      method: "post",
      endpoint: "/account/2fa/enable",
      payload: { code: values.code },
      onSuccess: (data) => {
        setSetup(null);
        setRecoveryCodes(data?.recoveryCodes || []);
        publish(true);
      },
    });
  };

  const confirmDisable = async () => {
    const values = await codeForm.validateFields();
    await mutate("disableTwoFactor", {
      method: "post",
      endpoint: "/account/2fa/disable",
      payload: { code: values.code },
      onSuccess: () => {
        setDisabling(false);
        codeForm.resetFields();
        publish(false);
      },
    });
  };

  const confirmRegenerate = async () => {
    const values = await codeForm.validateFields();
    await mutate("regenerateRecoveryCodes", {
      method: "post",
      endpoint: "/account/2fa/recovery-codes",
      payload: { code: values.code },
      onSuccess: (data) => {
        setRegenerating(false);
        codeForm.resetFields();
        setRecoveryCodes(data?.recoveryCodes || []);
        onChanged?.();
      },
    });
  };

  const codeField = (
    <Form form={codeForm} layout="vertical">
      <Form.Item
        name="code"
        label="Code from your authenticator app"
        rules={[{ required: true, message: "Required" }]}
      >
        <Input
          autoFocus
          placeholder="123456"
          inputMode="numeric"
          autoComplete="one-time-code"
          style={{ letterSpacing: 4, textAlign: "center" }}
        />
      </Form.Item>
    </Form>
  );

  return (
    <Card
      title={
        <Space>
          <SafetyCertificateOutlined />
          Two-step verification
          <Badge variant={enabled ? "success" : required ? "error" : "neutral"}>
            {enabled ? "On" : required ? "Required" : "Off"}
          </Badge>
        </Space>
      }
    >
      {required && !enabled && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Set this up to continue"
          description="This platform requires an authenticator app for web sign-in. Until you enrol one, only your profile is reachable."
        />
      )}

      {enabled ? (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Enabled on">
              {status?.enabledAtUtc
                ? dayjs(status.enabledAtUtc).format("DD MMM YYYY HH:mm")
                : "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Recovery codes left">
              <Text type={status?.recoveryCodesRemaining > 2 ? undefined : "warning"}>
                {status?.recoveryCodesRemaining ?? 0}
              </Text>
            </Descriptions.Item>
          </Descriptions>

          {status?.recoveryCodesRemaining <= 2 && (
            <Alert
              type="warning"
              showIcon
              message="You are running low on recovery codes"
              description="Generate a new set while you still have access to your authenticator app."
            />
          )}

          <Space wrap>
            <Button
              variant="secondary"
              icon={<ReloadOutlined />}
              onClick={() => {
                codeForm.resetFields();
                setRegenerating(true);
              }}
            >
              New recovery codes
            </Button>
            {/* Where 2FA is mandatory the API refuses to switch it off, so the action is
                not offered — an administrator reset is the only route to a new device. */}
            {!required && (
              <Button
                variant="danger"
                icon={<StopOutlined />}
                onClick={() => {
                  codeForm.resetFields();
                  setDisabling(true);
                }}
              >
                Turn off
              </Button>
            )}
          </Space>
        </Space>
      ) : (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Paragraph style={{ marginBottom: 0 }}>
            Add a second step to your sign-in with Google Authenticator, Microsoft
            Authenticator, 1Password or any other TOTP app. After your password you will be
            asked for the 6-digit code the app shows.
          </Paragraph>
          <Button
            variant="primary"
            icon={<MobileOutlined />}
            loading={loading?.startTwoFactorSetup}
            onClick={startSetup}
          >
            Set up authenticator
          </Button>
        </Space>
      )}

      {/* ---- Enrolment ---- */}
      <Modal
        open={Boolean(setup)}
        onClose={() => setSetup(null)}
        onConfirm={confirmSetup}
        title="Set up your authenticator"
        confirmText="Verify and enable"
        loading={loading?.enableTwoFactor}
        maskClosable={false}
        size="md"
      >
        <Steps
          direction="vertical"
          size="small"
          current={-1}
          items={[
            {
              title: "Scan the QR code",
              description: (
                <div style={{ padding: "8px 0" }}>
                  {setup?.qrCodeDataUri && (
                    <img
                      src={setup.qrCodeDataUri}
                      alt="Authenticator QR code"
                      style={{ width: 180, height: 180, borderRadius: 8, background: "#fff", padding: 8 }}
                    />
                  )}
                </div>
              ),
            },
            {
              title: "Or enter the key by hand",
              description: (
                <Text copyable code style={{ fontSize: 12, wordBreak: "break-all" }}>
                  {setup?.sharedKey}
                </Text>
              ),
            },
            {
              title: "Enter the code it generates",
              description: (
                <Form form={enableForm} layout="vertical" style={{ maxWidth: 220 }}>
                  <Form.Item
                    name="code"
                    rules={[{ required: true, message: "Required" }]}
                    style={{ marginBottom: 0, marginTop: 8 }}
                  >
                    <Input
                      placeholder="123456"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      style={{ letterSpacing: 4, textAlign: "center" }}
                    />
                  </Form.Item>
                </Form>
              ),
            },
          ]}
        />
      </Modal>

      {/* ---- Turn off ---- */}
      <Modal
        open={disabling}
        onClose={() => setDisabling(false)}
        onConfirm={confirmDisable}
        title="Turn off two-step verification"
        confirmText="Turn off"
        danger
        loading={loading?.disableTwoFactor}
        size="sm"
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Your account will be protected by its password alone"
        />
        {codeField}
      </Modal>

      {/* ---- New recovery codes ---- */}
      <Modal
        open={regenerating}
        onClose={() => setRegenerating(false)}
        onConfirm={confirmRegenerate}
        title="Generate new recovery codes"
        confirmText="Generate"
        loading={loading?.regenerateRecoveryCodes}
        size="sm"
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Your existing recovery codes stop working immediately"
        />
        {codeField}
      </Modal>

      <RecoveryCodesModal codes={recoveryCodes} onClose={() => setRecoveryCodes(null)} />
    </Card>
  );
};

export default TwoFactorCard;
